import { __rest, __assign } from 'tslib';
import * as React from 'react';
import LabcorpInvitaeLogoLight from '../../../assets/logos/LabcorpInvitaeLogoLight.esm.js';
import classNames from 'classnames';
import { kebabCase, toLower } from 'lodash';
import { NvMarkdownInline, NvMarkdown } from '@invitae/ids-react';
import { useCountryLocationPrefix } from '@invitae/nucleobase-engine-contentful';
import NavLink from '../../NavLink/NavLink.esm.js';
import SocialMediaLinks from '../SocialMediaLinks/SocialMediaLinks.esm.js';
import styles from './HardcodedFooter.module.esm.js';

var currentYear = new Date().getFullYear();
var footerNavLinks = [
    {
        header: 'Company',
        links: [
            {
                text: 'About',
                url: '/about',
            },
            {
                text: 'Careers',
                url: '/careers',
            },
            {
                text: 'Newsroom',
                url: 'https://ir.invitae.com/news-and-events/press-releases/default.aspx',
            },
            {
                text: 'Investors',
                url: 'https://ir.invitae.com/investor-relations/default.aspx',
            },
        ],
    },
    {
        header: 'Products',
        links: [
            {
                text: 'Oncology',
                url: '/providers/oncology/personalize-cancer-treatment',
            },
            {
                text: 'Women’s health',
                url: '/providers/obstetrics-gynecology/well-woman',
            },
            {
                text: 'Peds & Rare diseases',
                url: '/providers/test-catalog/pediatric-and-rare-diseases',
            },
            {
                text: 'Cardiology',
                url: '/providers/cardiology/genetic-testing-insights',
            },
            {
                text: 'Neurology',
                url: '/providers/test-catalog/neurology',
            },
            {
                text: 'Genetic risk',
                url: '/providers/test-catalog/genetic-risk',
            },
        ],
    },
    {
        header: 'Resources',
        links: [
            {
                text: 'Contact',
                url: '/contact',
            },
            {
                text: 'Clinical resources',
                url: '/providers/clinical-practice-resources',
            },
            {
                text: 'Tech & quality',
                url: '/generation',
            },
            {
                text: 'Education center',
                url: '/providers/education-center',
            },
            {
                text: 'Webinars',
                url: '/providers/educational-webinars',
            },
            {
                text: 'Patient stories',
                url: '/real-stories',
            },
            {
                text: 'Blog',
                url: 'https://blog.invitae.com',
            },
        ],
    },
];
var footerConfig = {
    legalLinks: [
        {
            id: 'cookiePreferences',
            text: 'Cookie preferences',
            url: '/privacy/cookie-policy',
        },
        {
            id: 'doNotSellMyInfo',
            text: 'Do not sell or share my personal information',
            url: '/do-not-sell-my-info',
        },
        {
            id: 'privacy',
            text: 'Privacy',
            url: '/privacy',
        },
        {
            id: 'termsOfUse',
            text: 'Terms of use',
            url: '/terms',
        },
        {
            id: 'accessibility',
            text: 'Accessibility',
            url: '/accessibility',
        },
        {
            id: 'licensing',
            text: 'Licensing',
            url: '/licensing',
        },
        {
            id: 'sitemap',
            text: 'Sitemap',
            url: '/sitemap',
        },
    ],
    text: {
        copyright: "\u00A9 ".concat(currentYear, " Labcorp. All rights reserved."),
        followUs: 'Follow us on',
    },
};
var HardcodedFooter = function (_a) {
    var props = __rest(_a, []);
    var className = classNames(styles.root, styles.dark);
    var addCountryCodePrefix = useCountryLocationPrefix().addCountryCodePrefix;
    var generateUrl = function (url) {
        return url.startsWith('http') ? url : addCountryCodePrefix(url);
    };
    var navGroups = footerNavLinks.map(function (navGroup, navGroupIndex) {
        return (React.createElement("div", { className: styles.navGroup, key: navGroupIndex },
            React.createElement(NvMarkdownInline, { elementWrapper: "h2" }, navGroup.header),
            React.createElement("ul", null, navGroup.links.map(function (_a, navLinkConfigIndex) {
                var url = _a.url, navLinkConfig = __rest(_a, ["url"]);
                return (React.createElement(NavLink, __assign({}, navLinkConfig, { dataCy: "footer_".concat(kebabCase(toLower(navLinkConfig.text)), "-link"), key: navLinkConfigIndex, url: generateUrl(url) })));
            }))));
    });
    return (React.createElement("footer", __assign({ className: className, "data-cy": "footer_root", "data-testid": "footer-root" }, props),
        React.createElement("div", { className: classNames('nv-container', styles.footerContainer) },
            React.createElement("div", { className: classNames('nv-grid', styles.footerContent) },
                React.createElement("div", { className: classNames('nv-fine-print-1', styles.about, styles.gridChild), "data-cy": "footer_logo" },
                    React.createElement(LabcorpInvitaeLogoLight, { idSuffix: "footer" }),
                    React.createElement("p", null, "Our mission is to bring comprehensive genetic information into mainstream medicine to improve healthcare for billions of people."),
                    React.createElement(SocialMediaLinks, { followUsText: footerConfig.text.followUs, isDesktop: true })),
                React.createElement("nav", { "aria-label": "Footer links", className: classNames(styles.listOfLinks, styles.gridChild, 'nv-grid') }, navGroups),
                React.createElement(SocialMediaLinks, { followUsText: footerConfig.text.followUs }),
                React.createElement("div", { className: classNames(styles.subFooter, styles.gridChild), "data-cy": "footer_copyright" },
                    React.createElement(NvMarkdown, { className: "nv-fine-print-1", "data-testid": "footer-copyright" }, footerConfig.text.copyright),
                    React.createElement("div", { className: styles.footerWrapper },
                        React.createElement("ul", { className: styles.legalLinks, "data-cy": "footer_legal_links" }, footerConfig.legalLinks.map(function (_a) {
                            var id = _a.id, text = _a.text, url = _a.url;
                            if (id === 'cookiePreferences') {
                                // The Cookiebot code (window.Cookiebot) may not exist in all SPA apps using the HardcodedFooter.
                                // Placeholder for future use when Cookiebot availability is guaranteed.
                                return React.createElement(NavLink, { dataCy: "footer_".concat(kebabCase(toLower(text)), "-link"), key: id, text: text, url: url });
                            }
                            else {
                                return React.createElement(NavLink, { dataCy: "footer_".concat(kebabCase(toLower(text)), "-link"), key: id, text: text, url: url });
                            }
                        }))))))));
};

export { HardcodedFooter as default, footerConfig, footerNavLinks };
//# sourceMappingURL=HardcodedFooter.esm.js.map
